<script>
import { defaultSelectedKeys } from "@/config/exportSchemas";

const STORAGE_PREFIX = "export_columns_";

export default {
  name: "ExportColumnPicker",
  props: {
    typeKey: {
      type: String,
      required: true,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    selected: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.persist(val);
      },
    },
    allKeys() {
      return this.columns.map((c) => c.key);
    },
    checkAll: {
      get() {
        return this.columns.length > 0 && this.selected.length === this.columns.length;
      },
      set(val) {
        this.selected = val ? this.allKeys.slice() : [];
      },
    },
    isIndeterminate() {
      return this.selected.length > 0 && this.selected.length < this.columns.length;
    },
  },
  watch: {
    typeKey: {
      immediate: true,
      handler() {
        this.restoreOrDefault();
      },
    },
    columns() {
      this.restoreOrDefault();
    },
  },
  methods: {
    storageKey() {
      return STORAGE_PREFIX + this.typeKey;
    },
    persist(keys) {
      try {
        localStorage.setItem(
          this.storageKey(),
          JSON.stringify({ selected: keys, known: this.allKeys })
        );
      } catch (e) {
        /* ignore */
      }
    },
    restoreOrDefault() {
      const valid = new Set(this.allKeys);
      const defaults = defaultSelectedKeys(this.columns).filter((k) =>
        valid.has(k)
      );
      let next = [];
      let known = [];
      try {
        const raw = localStorage.getItem(this.storageKey());
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            // legacy: plain key array
            next = parsed.filter((k) => valid.has(k));
            known = parsed.slice();
          } else if (parsed && Array.isArray(parsed.selected)) {
            next = parsed.selected.filter((k) => valid.has(k));
            known = Array.isArray(parsed.known) ? parsed.known : [];
          }
        }
      } catch (e) {
        next = [];
        known = [];
      }
      if (!next.length && !known.length) {
        next = defaults;
      } else {
        const knownSet = new Set(known);
        defaults.forEach((k) => {
          // auto-check newly introduced default columns
          if (!knownSet.has(k) && !next.includes(k)) {
            next.push(k);
          }
        });
      }
      this.$emit("input", next);
      this.persist(next);
    },
  },
};
</script>

<template>
  <div class="export-column-picker">
    <div class="mb-2 d-flex align-items-center">
      <el-checkbox v-model="checkAll" :indeterminate="isIndeterminate">
        {{ $t("export.columns.select_all") }}
      </el-checkbox>
      <span class="text-muted ml-3 small">
        {{ $t("export.columns.selected_count", { n: selected.length, total: columns.length }) }}
      </span>
    </div>
    <el-checkbox-group v-model="selected" class="export-column-group">
      <el-checkbox
        v-for="col in columns"
        :key="col.key"
        :label="col.key"
        class="export-column-item"
      >
        {{ $t(col.labelKey) }}
        <span v-if="col.sheet" class="text-muted small">
          ({{ $t("export.statistics.sheet_" + col.sheet) }})
        </span>
      </el-checkbox>
    </el-checkbox-group>
  </div>
</template>

<style scoped>
.export-column-group {
  display: flex;
  flex-wrap: wrap;
}
.export-column-item {
  width: 240px;
  margin-right: 0;
  margin-bottom: 8px;
}
</style>
